import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { Images, Trash, UploadSimple, VideoCamera, ArrowsClockwise, CaretLeft, CaretRight, PencilSimple, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const CarruselManager = ({ productos }) => {
  const keys = Object.keys(productos);
  const [sel, setSel] = useState(keys[0]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const dragIdx = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/landing/carrusel/${sel}`);
      setItems(r.data.items || []);
    } catch { toast.error('Error cargando carrusel'); }
    finally { setLoading(false); }
  }, [sel]);

  useEffect(() => { fetchItems(); setEditingId(null); }, [fetchItems]);

  const saveOrder = async (nuevos) => {
    setItems(nuevos);
    try {
      await api.put(`/landing/admin/carrusel/${sel}/orden`, { order: nuevos.map((it) => it.id) });
      toast.success('Orden guardado');
    } catch { toast.error('Error guardando el orden'); fetchItems(); }
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const nuevos = [...items];
    [nuevos[idx], nuevos[j]] = [nuevos[j], nuevos[idx]];
    saveOrder(nuevos);
  };

  const onDrop = (idx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === idx) return;
    const nuevos = [...items];
    const [moved] = nuevos.splice(from, 1);
    nuevos.splice(idx, 0, moved);
    saveOrder(nuevos);
  };

  const startEdit = (it) => { setEditingId(it.id); setEditValue(it.caption || ''); };

  const saveCaption = async (it) => {
    try {
      await api.patch(`/landing/admin/carrusel/${sel}/item/${it.id}`, { caption: editValue });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, caption: editValue.trim() } : x)));
      setEditingId(null);
      toast.success('Descripción actualizada');
    } catch { toast.error('Error guardando descripción'); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Solo se permiten imágenes o videos');
      return;
    }
    if (file.size > 80 * 1024 * 1024) {
      toast.error('Archivo muy grande (máx 80MB)');
      return;
    }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('caption', caption.trim());
      await api.post(`/landing/admin/carrusel/${sel}/item`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCaption('');
      toast.success(file.type.startsWith('video/') ? 'Video agregado al carrusel' : 'Foto agregada al carrusel');
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error subiendo archivo');
    } finally { setUploading(false); }
  };

  const onDelete = async (itemId) => {
    if (!window.confirm('¿Quitar este elemento del carrusel?')) return;
    try {
      await api.delete(`/landing/admin/carrusel/${sel}/item/${itemId}`);
      toast.success('Elemento eliminado');
      setItems((prev) => prev.filter((it) => it.id !== itemId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error eliminando');
    }
  };

  return (
    <Card className="p-4" data-testid="carrusel-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Images size={18} className="text-blue-400" />
          <p className="font-bold text-sm">Carruseles de las Landings</p>
        </div>
        <button onClick={fetchItems} data-testid="carrusel-refresh-btn"
          className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-white/15 hover:bg-white/5 transition-colors">
          <ArrowsClockwise size={13} /> Actualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {keys.map((k) => (
          <button key={k} onClick={() => setSel(k)} data-testid={`carrusel-tab-${k}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${sel === k ? 'bg-blue-500/20 text-blue-400 border-blue-400/50' : 'border-white/15 opacity-60 hover:opacity-100'}`}>
            {productos[k]}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="carrusel-caption-input"
          placeholder="Descripción de la nueva foto/video (opcional)"
          className="flex-1 min-w-[200px] bg-black/20 border border-white/15 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400 transition-colors" />
        <label className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border border-blue-400/40 text-blue-400 hover:bg-blue-400/10 transition-colors cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="carrusel-upload-btn">
          <UploadSimple size={14} /> {uploading ? 'Subiendo…' : 'Subir foto o video'}
          <input type="file" accept="image/*,video/*" className="hidden" onChange={onUpload} disabled={uploading} />
        </label>
      </div>

      {loading ? (
        <p className="text-xs opacity-50 py-6 text-center">Cargando…</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="carrusel-items-grid">
          {items.map((it, idx) => (
            <div key={it.id}
              draggable={editingId !== it.id}
              onDragStart={() => { dragIdx.current = idx; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              className="relative group rounded-xl overflow-hidden border border-white/10 bg-black/30 cursor-grab active:cursor-grabbing">
              {it.tipo === 'video' ? (
                <div className="relative">
                  <video src={it.url} className="w-full h-28 object-cover" muted preload="metadata" />
                  <span className="absolute top-1.5 left-1.5 bg-black/70 rounded-full p-1"><VideoCamera size={12} className="text-white" /></span>
                </div>
              ) : (
                <img src={it.url} alt={it.caption} className="w-full h-28 object-cover" loading="lazy" draggable={false} />
              )}
              <span className="absolute top-1.5 left-1/2 -translate-x-1/2 bg-black/70 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5">{idx + 1}</span>

              {editingId === it.id ? (
                <div className="flex items-center gap-1 p-1.5">
                  <input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') saveCaption(it); if (e.key === 'Escape') setEditingId(null); }}
                    data-testid={`carrusel-edit-input-${it.id}`}
                    placeholder="Descripción…"
                    className="flex-1 min-w-0 bg-black/40 border border-blue-400/50 rounded px-1.5 py-1 text-[10px] outline-none" />
                  <button onClick={() => saveCaption(it)} data-testid={`carrusel-edit-save-${it.id}`}
                    className="bg-emerald-500 hover:brightness-110 text-white rounded p-1"><Check size={11} /></button>
                </div>
              ) : (
                <button onClick={() => startEdit(it)} data-testid={`carrusel-edit-${it.id}`}
                  title="Editar descripción"
                  className="w-full flex items-center gap-1 text-left px-2 py-1.5 hover:bg-white/5 transition-colors">
                  <p className="text-[10px] opacity-70 truncate flex-1">{it.caption || 'Sin descripción'}</p>
                  <PencilSimple size={11} className="opacity-40 shrink-0" />
                </button>
              )}

              <div className="absolute inset-x-0 top-8 flex justify-between px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`carrusel-move-left-${it.id}`}
                  title="Mover a la izquierda"
                  className="bg-black/70 hover:bg-black text-white rounded-full p-1 disabled:opacity-30"><CaretLeft size={12} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`carrusel-move-right-${it.id}`}
                  title="Mover a la derecha"
                  className="bg-black/70 hover:bg-black text-white rounded-full p-1 disabled:opacity-30"><CaretRight size={12} /></button>
              </div>

              <button onClick={() => onDelete(it.id)} data-testid={`carrusel-delete-${it.id}`}
                title="Quitar del carrusel"
                className="absolute top-1.5 right-1.5 bg-red-500/90 hover:bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash size={12} />
              </button>
            </div>
          ))}
          {items.length === 0 && <p className="text-xs opacity-50 col-span-full py-4 text-center">Sin elementos</p>}
        </div>
      )}
      <p className="text-[10px] opacity-40 mt-3">Arrastra las tarjetas (o usa las flechas ‹ ›) para cambiar el orden. Haz clic en la descripción para editarla. Los cambios se reflejan al instante en la página pública. Fotos (JPG/PNG) y videos (MP4) hasta 80MB.</p>
    </Card>
  );
};
